import styled from '@emotion/styled'
import SVG from '../../assets/svg'
import React, { useState } from 'react'
import { font, space } from '../../styles'
import { AnimateWithVariants } from '../functional'
import { motion } from 'framer-motion'

interface Props {
   text: string | React.ReactNode
   type?: 'warning' | 'error' | 'success' | 'info'
   canHide?: boolean
   collapsibleDescription?: string | React.ReactNode
   autoHideTimeout?: number
   triggerAutoHide?: boolean
   className?: string
   isCollapsibleCollapsedByDefault?: boolean
   canCollapseDescription?: boolean
}

const Hint: React.FC<Props> = (props) => {
   const {
      text,
      type = 'warning',
      canHide,
      collapsibleDescription,
      triggerAutoHide,
      canCollapseDescription = true,
   } = props
   const [hidden, setHidden] = useState<boolean>(false)
   const [isHiding, setIsHiding] = useState<boolean>(false)
   const [collapsed, setCollapsed] = useState<boolean>(
      props.isCollapsibleCollapsedByDefault || false
   )

   // set default auto hide timeout
   const autoHideTimeout = props.autoHideTimeout || 4000

   // hide the hint after the animation is complete
   const hide = () => {
      setHidden(true)
   }

   // toggle the collapsible description
   const toggleCollapsed = () => {
      canCollapseDescription && setCollapsed(!collapsed)
   }

   const classNames = ` ${props.className || ''} ${
      canCollapseDescription && collapsibleDescription ? 'can-collapse-description' : ''
   }`

   if (hidden) return null
   return (
      <Holder
         className={classNames}
         onClick={toggleCollapsed}
         initial="collapseWithOpacity"
         animate={isHiding ? 'collapseWithOpacity' : 'expand'}
         onAnimationComplete={() => isHiding && hide()}
      >
         {triggerAutoHide && (
            <motion.span
               className={'time-out-loader ' + `${type}`}
               animate={{ width: `0%` }}
               initial={{ width: '100%' }}
               transition={{ duration: autoHideTimeout / 1000 }}
               onAnimationComplete={() => !isHiding && setIsHiding(true)}
            />
         )}
         <div className={'hint-holder ' + `${type} `}>
            <span className="icon">
               {type === 'warning' && <SVG.form.warning />}
               {type === 'error' && <SVG.form.error />}
               {type === 'success' && <SVG.form.success />}
               {type === 'info' && <SVG.form.lamp />}
            </span>
            {typeof text === 'string' && <p className="hint-text">{text}</p>}
            {typeof text !== 'string' && text}
            {canHide && !collapsibleDescription && (
               <span className="hide-button" onClick={() => setIsHiding(true)}>
                  <SVG.form.close />
               </span>
            )}
            {collapsibleDescription && canCollapseDescription && (
               <span className="collapsible-description-button">
                  {collapsed ? <SVG.form.downArrow /> : <SVG.form.upArrow />}
               </span>
            )}
         </div>
         {collapsibleDescription && (
            <AnimateWithVariants
               animate={collapsed ? 'collapse' : 'expand'}
               initial={collapsed ? 'collapse' : 'expand'}
            >
               <span className={'collapsible-description' + ' ' + `${type}`}>
                  <span className="collapsible-description-text">{collapsibleDescription}</span>
               </span>
            </AnimateWithVariants>
         )}
      </Holder>
   )
}
const Holder = styled(AnimateWithVariants)`
   position: relative;
   display: flex;
   flex-direction: column;
   margin-block: ${space.s};
   border-radius: ${space.xs};
   overflow: hidden;
   width: 100%;
   /* add slight shadow */
   box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.06);
   isolation: isolate;

   .time-out-loader {
      position: absolute;
      top: 0;
      left: 0;
      height: 3px;
      z-index: 2;
      width: 100%;

      &.warning {
         background-color: #ffd740;
      }

      &.error {
         background-color: #ff1200;
      }

      &.success {
         background-color: #007a42;
      }

      &.info {
         background-color: #0066ff;
      }
   }

   &.can-collapse-description {
      cursor: pointer;
   }

   .hint-holder {
      display: flex;
      align-items: center;
      gap: ${space.xs};
      padding: ${space.m} ${space.m};
      width: 100%;
      box-sizing: border-box;
      &.warning {
         color: #8a6e05;
         background-color: #fff3cd;
         .icon svg {
            fill: #b58f04;
         }
      }
      &.error {
         color: #d42417;
         background-color: #f8d7da;
         .icon svg {
            stroke: #ff1200;
         }
      }
      &.success {
         color: #007a42;
         background-color: #d4edda;
         .icon svg {
            stroke: #007a42;
         }
      }
      &.info {
         color: #0042a5;
         background-color: #cce5ff;
         .icon svg {
            stroke: #0066ff;
         }
      }
      .icon {
         position: relative;
         width: ${space.l};
         svg {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            height: 1.4em;
         }
      }
      .hint-text {
         margin: 0;
         color: inherit;
         ${font.regular};
         padding-inline: ${space.s};
         & :where(p, b, a, span) {
            color: inherit;
         }
      }
      .hide-button,
      .collapsible-description-button {
         cursor: pointer;
         margin-left: auto;
         position: relative;
         width: ${space.s};
         svg {
            width: 1.2em;
            fill: inherit;
            opacity: 0.3;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            height: 1.4em;
         }
         &:hover {
            svg {
               opacity: 0.7;
            }
         }
      }
   }

   .collapsible-description {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: ${space.m} ${space.xs};
      border-radius: 0 0 ${space.s} ${space.s};
      opacity: 1;

      p,
      b,
      a,
      span {
         color: inherit;
         ${font.thin};
      }
      a {
         text-decoration: underline;
      }

      &.warning {
         color: #ad8e1f;
         background-color: #fffbee;
      }
      &.error {
         color: #e2483d;
         background-color: #fff1f2;
      }
      &.success {
         color: #29ad71;
         background-color: #eefcf2;
      }
      &.info {
         color: #215cad;
         background-color: #e4f1ff;
      }

      .collapsible-description-text {
         ${font.thin};
         color: inherit;
         padding-inline: ${space.s};
      }
   }
`

export default Hint
